/**
 * Mirrors server/src/main/java/com/projectpets/common/NameRule.java field-for-field, including
 * its check order (leading/trailing space, then length, then charset), so the client can give
 * instant feedback before the round trip (SCREENS.md /companions/new "name-entry": 2-20 chars,
 * letters/digits/space/apostrophe/hyphen, no leading/trailing space). The server stays the
 * source of truth regardless — a 400 "Invalid field: name" still maps onto this same field via
 * deriveFormError (screens/formErrors.ts) no matter what this function decided locally.
 */
const ALLOWED = /^[\p{L}\p{N} '-]+$/u;
const MIN_LEN = 2;
const MAX_LEN = 20;

export const NAME_HELP =
  'Use 2–20 letters, numbers, spaces, apostrophes or hyphens — no leading or trailing space.';

/** True past the limit. QA 2026-09-23 onboard F3: the name fields used to carry `maxLength=20`,
 *  which silently dropped the 21st character and let the truncated name submit — so a screen
 *  shows the rule the moment this turns true, without waiting for blur or submit. */
export function nameTooLong(name: string): boolean {
  return Array.from(name).length > MAX_LEN;
}

/** Returns the help text if `name` fails the rule, or undefined if it's valid. */
export function validateName(name: string): string | undefined {
  if (name.startsWith(' ') || name.endsWith(' ')) return NAME_HELP;
  const len = Array.from(name).length; // code points, mirrors NameRule's codePointCount
  if (len < MIN_LEN || len > MAX_LEN) return NAME_HELP;
  if (!ALLOWED.test(name)) return NAME_HELP;
  return undefined;
}
